import type { ReactNode } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type { Appearance } from '@stripe/stripe-js';
import { Spinner } from '@anyroad/spinner';
import { Elements } from '@stripe/react-stripe-js';
import { colors, fontSizes } from '@anyroad/theme';
import { StripeContext } from './StripeContext';
import { useMemo } from 'react';

type StripeProviderProps = {
  /** Custom appearance configuration for Stripe Elements. Overrides default theme. */
  appearance?: Appearance;
  children: ReactNode;
  clientSecret: string | null;
  connectedAccountId: string | null;
  /** Custom loading spinner to show while initializing. Default: @anyroad/spinner */
  customLoadingSpinner?: ReactNode;
  publishableKey: string;
};

const defaultAppearance: Appearance = {
  theme: 'flat' as const,
  variables: {
    colorPrimary: colors.primary,
    colorBackground: 'transparent',
    colorText: colors.text.primary,
    colorTextPlaceholder: colors.text.placeholder,
    colorDanger: colors.error,
    fontFamily: 'proxima-nova, Helvetica, Arial, sans-serif',
    fontSizeBase: fontSizes.large,
    spacingUnit: '4px',
    borderRadius: '0px',
  },
  rules: {
    '.Input': {
      padding: '14px 16px',
      border: `1px solid ${colors.borderAction}`,
      borderRadius: '0px',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '.Input:hover': {
      border: `1px solid ${colors.borderAction}`,
    },
    '.Input:focus': {
      border: `1px solid ${colors.primary}`,
      outline: 'none',
      boxShadow: 'none',
    },
    '.Input::placeholder': {
      color: colors.text.placeholder,
    },
    '.Label': {
      fontSize: fontSizes.large,
      fontWeight: '600',
      marginBottom: '10px',
      color: colors.text.primary,
    },
  },
};

export const StripeProvider = ({
  appearance,
  children,
  clientSecret,
  connectedAccountId,
  customLoadingSpinner,
  publishableKey,
}: StripeProviderProps) => {

  const stripePromise = useMemo(() => {
    if (connectedAccountId) {
      return loadStripe(publishableKey, { stripeAccount: connectedAccountId });
    }
    return loadStripe(publishableKey);
  }, [publishableKey, connectedAccountId]);

  const options = useMemo(() => {
    // Merge custom appearance with default appearance
    const mergedAppearance: Appearance = appearance 
      ? {
          ...defaultAppearance,
          ...appearance,
          variables: {
            ...defaultAppearance.variables,
            ...appearance.variables,
          },
          rules: {
            ...defaultAppearance.rules,
            ...appearance.rules,
          },
        }
      : defaultAppearance;

    return {
      clientSecret: clientSecret || '',
      appearance: mergedAppearance,
    };
  }, [clientSecret, appearance]);

  return clientSecret ? (
    <StripeContext.Provider value={{ clientSecret }}>
      <Elements options={options} stripe={stripePromise}>
        {children}
      </Elements>
    </StripeContext.Provider>
  ) : (
    customLoadingSpinner || <Spinner />
  );
};
